import React from 'react';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  BookOpen,
  Plus,
  FileText,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { getEditionTypeInfo, getStatusBadge } from './editionTreeUtils.js';

interface EditionDetailPanelProps {
  selectedNode: Book;
  currentBookId: string;
  isDetailExpanded: boolean;
  onToggleExpanded: () => void;
  copiedUibn: string | null;
  onCopyUibn: (uibn: string, e?: React.MouseEvent) => void;
  selectedParentBook: Book | null;
  onOpenBookDetail?: (book: Book) => void;
  onRequestCreateEdition?: (parentBook: Book) => void;
}

export const EditionDetailPanel: React.FC<EditionDetailPanelProps> = ({
  selectedNode,
  currentBookId,
  isDetailExpanded,
  onToggleExpanded,
  copiedUibn,
  onCopyUibn,
  selectedParentBook,
  onOpenBookDetail,
  onRequestCreateEdition,
}) => {
  const selectedTypeInfo = getEditionTypeInfo(selectedNode.editionType);

  return (
    <div className="bg-white border-t-2 border-indigo-100 shadow-lg shrink-0 z-20 transition-all duration-200">
      {/* Toggle Bar / Bar Ringkas */}
      <div
        onClick={onToggleExpanded}
        className="px-3 sm:px-5 py-2.5 bg-slate-50 hover:bg-slate-100/80 border-b border-slate-200 flex items-center justify-between cursor-pointer transition select-none"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-indigo-600 shrink-0"></span>
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider shrink-0">
            Area Detail Edisi:
          </span>
          <span className="font-bold text-slate-900 text-xs sm:text-sm truncate">
            {selectedNode.title}
          </span>
          <span className="text-xs text-indigo-700 font-semibold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 shrink-0 hidden xs:inline">
            {selectedNode.edition || 'Edisi 1'}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
            {isDetailExpanded ? 'Sembunyikan Rincian' : 'Buka Rincian Lengkap'}
          </span>
          <button
            type="button"
            className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition cursor-pointer"
            aria-label={isDetailExpanded ? 'Tutup Detail' : 'Buka Detail'}
          >
            {isDetailExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-700" />
            ) : (
              <ChevronUp className="w-4 h-4 text-slate-700" />
            )}
          </button>
        </div>
      </div>

      {/* Expandable Full Detail Panel */}
      {isDetailExpanded && (
        <div className="p-3 sm:p-5 max-h-[48vh] sm:max-h-[38vh] overflow-y-auto space-y-3 bg-white">
          {/* Header info row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-2.5 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${selectedTypeInfo.badge}`}>
                  {selectedTypeInfo.label}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-xs font-bold">
                  {selectedNode.edition || 'Edisi 1'}
                </span>
                {getStatusBadge(selectedNode.status)}
                {selectedNode.id === currentBookId && (
                  <span className="text-[10px] bg-indigo-600 text-white font-bold px-2 py-0.5 rounded-full">
                    Naskah Sedang Aktif
                  </span>
                )}
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                {selectedNode.title}
              </h3>
            </div>

            {/* UIBN Box with one-click copy */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 flex items-center justify-between gap-3 shrink-0">
              <div>
                <span className="text-[10px] text-slate-400 block font-semibold uppercase">Nomor UIBN</span>
                <span className="font-mono text-xs font-bold text-slate-800">{selectedNode.uibn}</span>
              </div>
              {selectedNode.status !== 'diajukan' && (
                <button
                  type="button"
                  onClick={(e) => onCopyUibn(selectedNode.uibn, e)}
                  className="px-2 py-1 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-md text-xs font-semibold transition flex items-center gap-1 cursor-pointer"
                  title="Salin nomor UIBN"
                >
                  {copiedUibn === selectedNode.uibn ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-600" />
                      <span className="text-emerald-700">Tersalin</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Salin</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Main Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Box 1: Specifications */}
            <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-200/80 text-xs space-y-1.5">
              <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider block mb-1">
                Spesifikasi Penerbitan
              </span>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">Tahun Terbit:</span>
                <span className="font-semibold text-slate-800">{selectedNode.publicationYear}</span>
              </div>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">Jumlah Halaman:</span>
                <span className="font-semibold text-slate-800">{selectedNode.pages || 0} halaman</span>
              </div>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">Bahasa:</span>
                <span className="font-semibold text-slate-800">{selectedNode.language || 'Indonesia'}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-slate-500">Kategori:</span>
                <span className="font-semibold text-slate-800">{selectedNode.category}</span>
              </div>
            </div>

            {/* Box 2: Silsilah & Relasi Buku */}
            <div className="bg-slate-50/80 rounded-xl p-3 border border-slate-200/80 text-xs space-y-1.5">
              <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider block mb-1">
                Hubungan Silsilah Naskah
              </span>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">Status Silsilah:</span>
                <span className="font-semibold text-slate-800">
                  {!selectedNode.parentBookId ? 'Naskah Induk (Root)' : 'Edisi Turunan'}
                </span>
              </div>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">Buku Induk:</span>
                <span className="font-semibold text-slate-800 truncate max-w-[140px]" title={selectedParentBook?.title || 'Naskah Awal'}>
                  {selectedParentBook ? selectedParentBook.edition || 'Edisi Induk' : 'Naskah Pertama'}
                </span>
              </div>
              <div className="flex justify-between py-0.5 border-b border-slate-200/50">
                <span className="text-slate-500">ISBN Induk/Asli:</span>
                <span className="font-mono font-medium text-slate-800">{selectedNode.isbnOriginal || '-'}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-slate-500">Penerbit Resmi:</span>
                <span className="font-semibold text-slate-800 truncate max-w-[140px]">{selectedNode.publisherName}</span>
              </div>
            </div>

            {/* Box 3: Catatan Perubahan Edisi (Changelog) */}
            <div className="bg-amber-50/70 rounded-xl p-3 border border-amber-200/80 text-xs space-y-1.5 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-amber-900 font-bold mb-1">
                  <FileText className="w-3.5 h-3.5 text-amber-700" />
                  <span className="text-[11px] uppercase tracking-wider">Catatan Revisi & Perubahan</span>
                </div>
                {selectedNode.editionNotes ? (
                  <p className="text-xs text-slate-800 leading-relaxed italic bg-white/80 p-2.5 rounded-lg border border-amber-200/60 font-medium">
                    "{selectedNode.editionNotes}"
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 italic bg-white/60 p-2 rounded-lg border border-amber-100">
                    Naskah awal orisinal tanpa catatan revisi tambahan.
                  </p>
                )}
              </div>
              <div className="text-[10px] text-amber-800/80 font-medium pt-1">
                Setiap perubahan edisi terdokumentasi dalam sistem registrasi UIBN nasional.
              </div>
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2 border-t border-slate-100">
            {onOpenBookDetail && (
              <button
                type="button"
                onClick={() => onOpenBookDetail(selectedNode)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-xl text-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <BookOpen className="w-3.5 h-3.5 text-indigo-600" />
                <span>Buka Informasi Lengkap Buku</span>
              </button>
            )}

            {onRequestCreateEdition && (
              <button
                type="button"
                onClick={() => onRequestCreateEdition(selectedNode)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs transition flex items-center justify-center gap-1.5 shadow-xs cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Terbitkan Edisi Baru dari Naskah Ini</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
